import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Copy, Check, ExternalLink, DollarSign, Repeat, Users, Newspaper,
  ChevronLeft, ChevronRight, BookOpen, MessageSquareQuote, ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROF_LABELS = {
  medico: 'Medico',
  veterinario: 'Veterinario',
  naturopata: 'Naturopata',
  operatore_olistico: 'Operatore olistico',
  erborista_parafarmacia: 'Erborista / parafarmacia',
  informatore_farmaceutico: 'Informatore farmaceutico',
  marketer_influencer: 'Marketer / influencer',
};

// Pannelli chiari su avorio, come le sezioni-documento del sito: dodici
// riquadri neri in fila pesano troppo per uno strumento di lavoro.
const CARD_STYLES = [
  { bg: 'bg-[#efe8da]', text: 'text-[#12100c]', iconBg: 'bg-[#12100c]/[0.06]' },
];

// Provvigioni non ancora arrivate, o AffiliateWP che non risponde: si scrive
// "n/d". Lo zero invece si scrive zero, perché lo zero è un'informazione.
const NON_DISPONIBILE = 'n/d';

const disponibili = (c) => c && c.linked && c.available;

const numero = (c, v) => (disponibili(c) ? String(v ?? 0) : NON_DISPONIBILE);

const euro = (c, v) =>
  disponibili(c)
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR',
        maximumFractionDigits: 0 }).format(Number(v) || 0)
    : NON_DISPONIBILE;

/** Finché la risposta non è arrivata si lascia il posto vuoto, non "n/d". */
const ATTESA = '...';

const valoreRiordini = (r, calcola) => {
  if (r === null) return ATTESA;
  if (!r.linked || !r.available) return NON_DISPONIBILE;
  return calcola(r);
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const profile = user?.profile;
  const isMedico = profile?.professional_type === 'medico';
  const isStaff = user?.is_admin || user?.is_editor || user?.role === 'admin' || user?.role === 'editor';
  const [copied, setCopied] = useState(false);
  const [areas, setAreas] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [commissioni, setCommissioni] = useState(null);
  const [riordini, setRiordini] = useState(null);
  const scrollRef = useRef(null);

  // Senza codice non si stampa "null": si mostra soltanto quello che esiste.
  const affiliateLink = profile?.affiliate_code
    ? `https://biounica.it?ref=${profile.affiliate_code}`
    : '';

  useEffect(() => {
    axios.get(`${API}/application-areas`).then(r => setAreas(r.data)).catch(() => {});
    axios.get(`${API}/announcements?limit=5`).then(r => setAnnouncements(r.data)).catch(() => {});
    // I numeri veri delle card: senza risposta certa da AffiliateWP si scrive
    // "n/d", mentre lo zero resta uno zero perché è un'informazione.
    axios.get(`${API}/me/commissions`)
      .then(r => setCommissioni(r.data))
      .catch(() => setCommissioni({ linked: true, available: false }));
    // I riordini passano da WooCommerce e ci mettono qualche secondo: stanno
    // su una chiamata a parte, così il resto della dashboard non aspetta.
    axios.get(`${API}/me/riordini`)
      .then(r => setRiordini(r.data))
      .catch(() => setRiordini({ linked: true, available: false }));
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(profile?.affiliate_code || '');
      setCopied(true);
      toast.success('Codice copiato!');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Impossibile copiare'); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLink);
      toast.success('Link copiato!');
    } catch { toast.error('Impossibile copiare'); }
  };

  const scrollCarousel = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.6;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-5xl" data-testid="dashboard-page">
      {/* Greeting */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]" data-testid="dashboard-greeting">
          Benvenuto, {profile?.full_name?.split(' ')[0]}
        </h1>
        <div className="flex items-center gap-2 mt-2">
          <Badge className="bg-[#c6a15b]/10 text-[#8a6620] hover:bg-[#c6a15b]/20 border-none font-medium text-xs" data-testid="professional-type-badge">
            {PROF_LABELS[profile?.professional_type] || profile?.professional_type}
          </Badge>
        </div>
      </div>

      {/* Application Areas Carousel/Grid */}
      {areas.length > 0 && (
        <div data-testid="areas-carousel-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg sm:text-xl font-semibold text-[#12100c]">
              Aree di applicazione di UMIFUL
            </h2>
            <div className="flex gap-1 lg:hidden">
              <button onClick={() => scrollCarousel('left')} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="carousel-prev-btn">
                <ChevronLeft className="w-4 h-4 text-[#12100c]" />
              </button>
              <button onClick={() => scrollCarousel('right')} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="carousel-next-btn">
                <ChevronRight className="w-4 h-4 text-[#12100c]" />
              </button>
            </div>
          </div>

          {/* Mobile: scrollable, Desktop: grid */}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {areas.map((area, i) => {
              const style = CARD_STYLES[i % CARD_STYLES.length];
              return (
                <button
                  key={area.slug}
                  onClick={() => navigate(`/aree-applicazione#${area.slug}`)}
                  className={`snap-start shrink-0 w-[46%] sm:w-[30%] lg:w-auto ${style.bg} border border-[color:var(--line)] rounded-2xl p-4 text-left transition-all duration-200 hover:border-[#8a6620]/40 group`}
                  data-testid={`area-card-${area.slug}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${style.iconBg} flex items-center justify-center mb-3 text-xl`}>
                    {area.icon}
                  </div>
                  <h3 className={`font-semibold text-sm leading-tight mb-1 ${style.text}`}>
                    {area.title}
                  </h3>
                  <p className={`text-[11px] leading-snug ${style.text} opacity-75`}>
                    {area.short_description}
                  </p>
                  {area.priority === 'alta' && (
                    <span className="inline-block mt-2 text-[9px] font-semibold uppercase tracking-wider bg-[#c6a15b]/20 text-[#8a6620] px-1.5 py-0.5 rounded">
                      Alta priorità
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Conosci UMIFUL - CTA (solo staff) */}
      {isStaff && (
      <Card className="rounded-2xl border-none shadow-sm overflow-hidden" data-testid="know-umiful-cta">
        <div className="bg-gradient-to-br from-[#12100c] to-[#1a1712] p-5 sm:p-7">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="flex-1">
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-2">
                Perché UMIFUL è diverso da tutti gli altri integratori
              </h2>
              <p className="text-sm text-white/80 leading-relaxed mb-3">
                UMIFUL non aggiunge una vitamina o un minerale specifico. Ripristina ciò che manca nel cibo moderno:
                gli acidi umici e fulvici, sostanze che per millenni erano presenti naturalmente nel suolo e che
                l'agricoltura intensiva ha eliminato. Agisce a un livello più profondo: migliora il trasporto
                dei nutrienti dentro le cellule, riequilibra il microbiota, chelata le tossine e riduce l'infiammazione.
                È il "terreno fertile" su cui tutti gli altri integratori funzionano meglio.
              </p>
              <Button
                onClick={() => navigate('/conosci-umiful')}
                className="bg-[#c6a15b] hover:bg-[#a07c34] text-[#12100c] rounded-full px-5 h-9 text-sm"
                data-testid="know-umiful-cta-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" /> Apri la documentazione
              </Button>
            </div>
            <div className="hidden sm:flex flex-col gap-2 text-white/60 text-xs shrink-0 w-48">
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c6a15b]" /> Trasporto cellulare dei nutrienti</div>
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c6a15b]" /> Chelazione metalli pesanti</div>
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c6a15b]" /> Modulazione del microbiota</div>
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c6a15b]" /> Azione antiossidante potente</div>
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c6a15b]" /> Immunomodulazione</div>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* La riga in cima: il guadagno resta, ma affiancato dalla ricorrenza
          dei pazienti, che è la misura di come sta andando davvero. */}
      {!isMedico && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-section">
          {[
            {
              label: 'Provvigioni maturate',
              value: euro(commissioni, commissioni?.earnings),
              icon: DollarSign, color: '#8a6620',
            },
            {
              label: 'Pazienti che hanno riordinato',
              value: valoreRiordini(riordini, (r) => `${r.clienti_con_riordino} su ${r.clienti}`),
              icon: Repeat, color: '#5f7d52',
            },
            {
              label: 'Tasso di riordino',
              value: valoreRiordini(riordini, (r) => (r.clienti ? `${r.tasso_riordino}%` : '0%')),
              icon: Repeat, color: '#5f7d52',
            },
            {
              label: 'Vendite totali',
              value: numero(commissioni, commissioni?.referrals_count),
              icon: Users, color: '#12100c',
            },
          ].map((stat, i) => (
            <Card key={i} className="rounded-2xl border-gray-100 shadow-sm" data-testid={`stat-card-${i}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="mb-3"><stat.icon className="w-5 h-5" style={{ color: stat.color }} strokeWidth={1.5} /></div>
                <p className="text-2xl font-bold text-[#12100c]">{stat.value}</p>
                <p className="text-[11px] text-[#4a4238] mt-1 leading-tight">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Riga di servizio: il codice resta a portata, senza occupare la scena */}
      {profile?.affiliate_code && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
          data-testid="affiliate-code-row">
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#8a6620]">Il tuo codice</span>
          <span className="font-mono text-sm font-medium tracking-wider text-[#12100c]" data-testid="affiliate-code-display">
            {profile.affiliate_code}
          </span>
          <button onClick={copyCode} className="text-[#4a5f43] hover:text-[#12100c] transition-colors" data-testid="copy-code-btn">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <span className="hidden sm:inline text-[color:var(--line)]">|</span>
          <span className="text-xs text-[#5b5346] truncate max-w-[16rem]">{affiliateLink}</span>
          <button onClick={copyLink} className="text-[#4a5f43] hover:text-[#12100c] transition-colors shrink-0" data-testid="copy-link-btn">
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Il canale di ritorno, dove chi entra lo vede */}
      <Card className="rounded-2xl border-gray-100 shadow-sm" data-testid="racconta-caso-cta">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#5f7d52]/10 flex items-center justify-center shrink-0">
            <MessageSquareQuote className="w-5 h-5 text-[#4a5f43]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm text-[#12100c]">Hai un caso da raccontare?</p>
            <p className="text-xs text-[#4a4238] mt-0.5 leading-relaxed">
              Quello che osservi nella tua pratica è la cosa che ci manca di più. Bastano poche righe.
            </p>
          </div>
          <Button onClick={() => navigate('/racconta-un-caso')}
            className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full h-9 px-4 text-sm shrink-0"
            data-testid="racconta-caso-btn">
            Racconta un caso <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </CardContent>
      </Card>

      {/* News */}
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-semibold text-[#12100c] mb-3" data-testid="news-title">Novità</h2>
        <div className="space-y-3">
          {announcements.length > 0 ? announcements.map(a => {
            const typeColors = { info: 'bg-blue-50 text-blue-600', update: 'bg-[#5f7d52]/10 text-[#4a5f43]', important: 'bg-red-50 text-red-600', event: 'bg-[#c6a15b]/10 text-[#8a6620]' };
            return (
              <Card key={a.id} className={`rounded-2xl shadow-sm ${a.is_pinned ? 'border-[#c6a15b]/30' : 'border-gray-100'}`} data-testid={`announcement-${a.id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex gap-3">
                    <span className="text-xl shrink-0">{a.icon || '\U0001f4e2'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-[#12100c] truncate">{a.title}</p>
                        <Badge className={`text-[8px] border-none ${typeColors[a.type] || typeColors.info}`}>{a.type}</Badge>
                        {a.is_pinned && <Badge className="text-[8px] bg-[#c6a15b] text-[#12100c] border-none">Fissato</Badge>}
                      </div>
                      <p className="text-xs text-[#4a4238] leading-relaxed line-clamp-2">{a.content}</p>
                      {a.link_url && <a href={a.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4a5f43] hover:underline mt-1 inline-block">{a.link_text || 'Scopri di più'}</a>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }) : (
            <Card className="rounded-2xl border-gray-100 shadow-sm" data-testid="news-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#5f7d52]/10 flex items-center justify-center shrink-0">
                    <Newspaper className="w-5 h-5 text-[#4a5f43]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#12100c]">Benvenuto nel portale Biounica Pro!</p>
                    <p className="text-xs text-[#4a4238] mt-1 leading-relaxed">
                      Il portale è in fase di aggiornamento. Nuovi contenuti e funzionalità saranno disponibili a breve.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
